/* eslint-disable react/no-unused-prop-types */
import { useCallback, useMemo } from 'react';
import { UniversalPickerOption } from '@rainbow-modules/forms';
import { useReduxForm } from '@rainbow-modules/hooks';
import { ChannelPickerProps, ChannelType } from './types';
import { StyledUniversalPicker, PickerOptionContainer } from './styled';
import TooltipWrapper from './tooltipWrapper';

const ChannelPicker = (props: ChannelPickerProps) => {
    const {
        value: valueInProps = 'all',
        onChange = () => {},
        id,
        className,
        name,
        label,
        error,
        required,
        channels = [],
        hideOptionLabel = false,
    } = useReduxForm(props);

    const allChannels = useMemo(() => [
        {
            channel: 'all',
            label: 'All',
        },
        ...channels,
    ], [channels]);

    const pickerOptions = useMemo(
        () => allChannels.map(
            (channel) => {
                const hasIcon = 'icon' in channel && channel.icon;

                const shouldShowLabel = !hideOptionLabel || !hasIcon;

                const isIconOnly = hideOptionLabel && hasIcon;

                const optionContent = (
                    <UniversalPickerOption
                        key={`channel-picker-option__${channel.channel}`}
                        name={channel.channel}
                        component={PickerOptionContainer}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        channel={channel.channel as ChannelType}
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        isIconOnly={isIconOnly}
                    >
                        {hasIcon && <span>{channel.icon}</span>}
                        {shouldShowLabel && <span>{channel.label}</span>}
                    </UniversalPickerOption>
                );

                if (isIconOnly) {
                    return (
                        <TooltipWrapper
                            key={`tooltip-wrapper__${channel.channel}`}
                            tooltipLabel={channel.label}
                        >
                            {optionContent}
                        </TooltipWrapper>
                    );
                }

                return optionContent;
            },
        ),
        [allChannels, hideOptionLabel],
    );

    const handleOnChange = useCallback(
        (newValue: string | string[]) => {
            const channelId = typeof newValue === 'string' ? newValue : newValue[0];
            const found = allChannels.find((channel) => channel.channel === channelId);
            if (found) {
                onChange(found.channel as ChannelType);
            }
        },
        [allChannels, onChange],
    );

    return (
        <StyledUniversalPicker
            id={id}
            name={name}
            className={className}
            label={label}
            labelAlignment="left"
            required={required}
            value={valueInProps}
            onChange={handleOnChange}
            error={error}
        >
            {pickerOptions}
        </StyledUniversalPicker>
    );
};

export default ChannelPicker;
